import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { assetService, AssetType, Asset, AssetTypeField } from '@/firebase/assetService';
import { complianceService, ComplianceTemplate } from '@/firebase/complianceService';
import { locationService, Location } from '@/firebase/locationService';
import { floorService, Floor } from '@/firebase/floorService';
import { useNotification } from '@/contexts/NotificationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useAuth } from '@/contexts/AuthContext';
import { usePreview } from '@/contexts/PreviewContext';
import { 
  fetchUserRoles, 
  getUserSystemRoleForOrganization,
  getUserOwnerRolesForOrganization,
  isManagerInOrganization,
  isSystemUserInOrganization
} from '@/utils/roleHelpers';
import { SYSTEM_ROLES } from '@/constants/roles';
import { aiSearchService, NewAssetTypeSuggestion } from '@/services/aiSearchService';
import { Plus, Trash2, Pencil, X, Package, Eye, Image, ChevronUp, ChevronDown, Search, ExternalLink, Sparkles, Download, AlertCircle, MapPin } from 'lucide-react';
import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import AssetTypeForm from './AssetTypeForm';
import AssetForm from './AssetForm';
import BulkUploadAsset from './BulkUploadAsset';
import FeatureLock from './FeatureLock';
import ContextualHelp from './ContextualHelp';
import AssetFloorTagModal from './AssetFloorTagModal';

const AssetManagement: React.FC = () => {
  const { showNotification } = useNotification();
  const { currentOrganization } = useOrganization();
  const { previewState, isPreviewMode } = usePreview();
  const { hasFeature } = useSubscription();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [assetTypes, setAssetTypes] = useState<AssetType[]>([]);
  const [templates, setTemplates] = useState<Record<string, ComplianceTemplate>>({});
  const [assetCounts, setAssetCounts] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAssetType, setSelectedAssetType] = useState<AssetType | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isAddingAsset, setIsAddingAsset] = useState(false);
  const [assetTypeForAsset, setAssetTypeForAsset] = useState<AssetType | null>(null);
  const [isBulkUploading, setIsBulkUploading] = useState(false);
  const [isViewingAssets, setIsViewingAssets] = useState(false);
  const [assetsList, setAssetsList] = useState<Asset[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [filterLocation, setFilterLocation] = useState<string>('all');
  const [newAssetTypeSuggestions, setNewAssetTypeSuggestions] = useState<NewAssetTypeSuggestion[] | null>(null);
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [assetTypeToAdd, setAssetTypeToAdd] = useState<Partial<AssetType> | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isQRSheetModalOpen, setIsQRSheetModalOpen] = useState(false);
  const [selectedQRSheetLocation, setSelectedQRSheetLocation] = useState<string>('');
  const [isGeneratingQRSheet, setIsGeneratingQRSheet] = useState(false);
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [showDeleteAssetTypeConfirm, setShowDeleteAssetTypeConfirm] = useState(false);
  const [assetTypeToDelete, setAssetTypeToDelete] = useState<string | null>(null);
  const [userOwnerRoles, setUserOwnerRoles] = useState<string[]>([]);
  const [isSystemUser, setIsSystemUser] = useState<boolean>(false);
  const [isSystemManager, setIsSystemManager] = useState<boolean>(false);
  const [userLocationIds, setUserLocationIds] = useState<string[]>([]);

  useEffect(() => {
    loadUserRoles();
    loadUserLocations();
  }, [currentOrganization?.id, user?.id, isPreviewMode, previewState?.previewType, previewState?.previewUserId, previewState?.role]);

  useEffect(() => {
    loadData();
  }, [currentOrganization?.id, user?.id, userLocationIds.length, isSystemUser]);

  const loadUserRoles = async () => {
    const organizationId = currentOrganization?.id;
    
    // Need organization ID to check roles
    if (!organizationId && !isPreviewMode) {
      setUserOwnerRoles([]);
      setIsSystemUser(false);
      setIsSystemManager(false);
      return;
    }

    // In preview mode, use preview user's roles
    if (isPreviewMode && previewState?.previewType === 'user' && previewState.previewUserId) {
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const previewUserRef = doc(db, 'users', previewState.previewUserId);
        const previewUserSnap = await getDoc(previewUserRef);
        
        if (previewUserSnap.exists()) {
          const previewUserData = previewUserSnap.data();
          
          // Use organization-specific role helpers if organizationId is available
          if (organizationId) {
            const ownerRoles = getUserOwnerRolesForOrganization(previewUserData, organizationId);
            setUserOwnerRoles(ownerRoles);
            setIsSystemUser(isSystemUserInOrganization(previewUserData, organizationId));
            setIsSystemManager(isManagerInOrganization(previewUserData, organizationId));
          } else {
            // Fallback to global roles if no organization
            const previewUserRoles = previewUserData.roles || (previewUserData.role ? [previewUserData.role] : []);
            const ownerRoles = previewUserRoles.filter((r: string) => !SYSTEM_ROLES.includes(r.toLowerCase() as any));
            setUserOwnerRoles(ownerRoles);
            const systemRoles = previewUserRoles.filter((r: string) => SYSTEM_ROLES.includes(r.toLowerCase() as any));
            const isSystemUserRole = systemRoles.length === 1 && systemRoles[0].toLowerCase() === 'user';
            setIsSystemUser(isSystemUserRole);
            const isManagerRole = previewUserRoles.some((r: string) => ['manager', 'admin'].includes(r.toLowerCase()));
            setIsSystemManager(isManagerRole);
          }
        }
      } catch (error) {
        console.error('Error loading preview user roles:', error);
      }
    } else if (isPreviewMode && previewState?.previewType === 'role') {
      // Preview by role: determine permissions based on role
      const role = previewState.role.toLowerCase();
      const isSystemUserRole = role === 'user';
      const isManagerRole = role === 'manager' || role === 'admin';
      
      setIsSystemUser(isSystemUserRole);
      setIsSystemManager(isManagerRole);
      setUserOwnerRoles([]); // No owner roles when previewing by role
    } else if (user?.id && organizationId) {
      // Normal mode: use actual user's roles with organization-specific checks
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const userRef = doc(db, 'users', user.id);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          
          // Use organization-specific role helpers
          const ownerRoles = getUserOwnerRolesForOrganization(userData, organizationId);
          setUserOwnerRoles(ownerRoles);
          setIsSystemUser(isSystemUserInOrganization(userData, organizationId));
          setIsSystemManager(isManagerInOrganization(userData, organizationId));
        } else {
          // Fallback to old method if user not found
          const roles = await fetchUserRoles(user.id);
          setUserOwnerRoles(roles.ownerRoles);
          setIsSystemUser(roles.isSystemUser);
          setIsSystemManager(roles.isSystemManager);
        }
      } catch (error) {
        console.error('Error loading user roles:', error);
        // Fallback to old method on error
        const roles = await fetchUserRoles(user.id);
        setUserOwnerRoles(roles.ownerRoles);
        setIsSystemUser(roles.isSystemUser);
        setIsSystemManager(roles.isSystemManager);
      }
    } else if (user?.id) {
      // Fallback: use global roles if no organization
      const roles = await fetchUserRoles(user.id);
      setUserOwnerRoles(roles.ownerRoles);
      setIsSystemUser(roles.isSystemUser);
      setIsSystemManager(roles.isSystemManager);
    }
  };

  const loadUserLocations = async () => {
    // In preview mode, use preview user's locationIds
    if (isPreviewMode && previewState?.previewType === 'user' && previewState.locationIds) {
      setUserLocationIds(previewState.locationIds);
    } else if (isPreviewMode && previewState?.previewType === 'role') {
      // Preview by role: no location restrictions (show all)
      setUserLocationIds([]);
    } else if (user?.id && currentOrganization?.id) {
      // Normal mode: use actual user's locationIds
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const userRef = doc(db, 'users', user.id);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          const locationIds = userData.locationIds || [];
          setUserLocationIds(locationIds);
        }
      } catch (error) {
        console.error('Error loading user locations:', error);
        setUserLocationIds([]);
      }
    } else {
      setUserLocationIds([]);
    }
  };

  // Debounced AI search when no results are found
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Clear AI suggestion when search term is cleared
    if (!searchTerm) {
      setNewAssetTypeSuggestions(null);
      return;
    }

    // Calculate filtered asset types inline to check if we have results
    const currentFiltered = assetTypes.filter(assetType =>
      assetType.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      assetType.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Only trigger AI search if there are no filtered asset types and search term is long enough
    const hasResults = currentFiltered.length > 0;
    
    if (!hasResults && searchTerm.length >= 3) {
      // Debounce AI search by 800ms
      searchTimeoutRef.current = setTimeout(async () => {
        setIsLoadingAI(true);
        setNewAssetTypeSuggestions(null);
        
        try {
          // Get available templates
          const availableTemplates = Object.values(templates);
          
          // Check if user has any templates first
          if (availableTemplates.length === 0) {
            // No templates available - can't create asset types without templates
            setIsLoadingAI(false);
            setNewAssetTypeSuggestions([] as any);
            return;
          }
          
          const existingAssetTypeNames = assetTypes.map(at => at.name);
          
          // Try AI generation for asset types
          const newSuggestions = await aiSearchService.generateNewAssetTypeSuggestions(
            searchTerm, 
            existingAssetTypeNames,
            availableTemplates
          );
          
          if (newSuggestions && newSuggestions.length > 0) {
            setNewAssetTypeSuggestions(newSuggestions);
          } else {
            // No suggestions from AI
            setNewAssetTypeSuggestions([] as any);
          }
        } catch (error) {
          console.error('Error fetching AI asset type suggestions:', error);
        } finally {
          setIsLoadingAI(false);
        }
      }, 800);
    } else {
      setNewAssetTypeSuggestions(null);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, assetTypes, templates]);

  const loadData = async () => {
    // Don't load data if no organization
    if (!currentOrganization?.id) {
      setTemplates({});
      setAssetTypes([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    
    // Load locations first (non-deleted only) so we can filter assets by them
    const locationsResult = await locationService.getAllLocations(currentOrganization.id);
    const nonDeletedLocations = locationsResult.success && locationsResult.data ? locationsResult.data : [];
    setLocations(nonDeletedLocations);
    const nonDeletedLocationIds = new Set(nonDeletedLocations.map(loc => loc.id).filter(Boolean));
    
    // Load templates for current organization
    const templatesResult = await complianceService.getAllTemplates(currentOrganization.id);
    if (templatesResult.success && templatesResult.data) {
      const templateMap: Record<string, ComplianceTemplate> = {};
      templatesResult.data.forEach(template => {
        if (template.id) {
          templateMap[template.id] = template;
        }
      });
      setTemplates(templateMap);
    }
    
    // Load asset types for current organization
    const result = await assetService.getAllAssetTypes(currentOrganization.id);
    if (result.success && result.data) {
      setAssetTypes(result.data);
      
      // Load asset counts for each asset type (only count assets in non-deleted locations)
      const counts: Record<string, number> = {};
      for (const assetType of result.data) {
        if (assetType.id) {
          const assetsResult = await assetService.getAssetsByType(assetType.id, currentOrganization.id);
          if (assetsResult.success && assetsResult.data) {
            let assets = assetsResult.data.filter(asset =>
              asset.locationId && nonDeletedLocationIds.has(asset.locationId)
            );
            if (isSystemUser && userLocationIds.length > 0) {
              assets = assets.filter(asset => userLocationIds.includes(asset.locationId));
            }
            counts[assetType.id] = assets.length;
          } else {
            counts[assetType.id] = 0;
          }
        }
      }
      setAssetCounts(counts);
    } else {
      showNotification(`Error loading asset types: ${result.error}`, 'error');
    }
    
    setIsLoading(false);
  };

  const handleAdd = () => {
    // Check if user has permission to add asset types (only admin or manager)
    if (!isSystemManager) {
      showNotification('Only administrators and managers can add asset types', 'error');
      return;
    }
    if (!currentOrganization?.id) {
      setShowOrgModal(true);
      return;
    }
    setSelectedAssetType(null);
    setAssetTypeToAdd(null);
    setIsAdding(true);
    setIsEditing(false);
  };

  const handleEdit = (assetType: AssetType) => {
    // Check if user has permission to edit asset types (only admin or manager)
    if (!isSystemManager) {
      showNotification('Only administrators and managers can edit asset types', 'error');
      return;
    }
    setSelectedAssetType(assetType);
    setIsEditing(true);
  };

  const handleDelete = (id: string) => {
    // Check if user has permission to delete asset types (only admin or manager)
    if (!isSystemManager) {
      showNotification('Only administrators and managers can delete asset types', 'error');
      return;
    }
    setAssetTypeToDelete(id);
    setShowDeleteAssetTypeConfirm(true);
  };

  const handleDeleteAssetTypeConfirm = async () => {
    if (!assetTypeToDelete) return;
    
    // Check if user has permission to delete (only admin or manager)
    if (!isSystemManager) {
      showNotification('Only administrators and managers can delete asset types', 'error');
      setShowDeleteAssetTypeConfirm(false);
      setAssetTypeToDelete(null);
      return;
    }
    
    const result = await assetService.deleteAssetType(assetTypeToDelete);
    if (result.success) {
      await loadData();
      showNotification('Asset type deleted successfully', 'success');
    } else {
      showNotification(`Error: ${result.error}`, 'error');
    }
    setShowDeleteAssetTypeConfirm(false);
    setAssetTypeToDelete(null);
  };

  const handleFormSubmit = async (data: Partial<AssetType>) => {
    // Check if user has permission (only admin or manager)
    if (!isSystemManager) {
      showNotification('Only administrators and managers can add or edit asset types', 'error');
      setIsAdding(false);
      setIsEditing(false);
      return;
    }

    if (isAdding) {
      if (!currentOrganization?.id) {
        setShowOrgModal(true);
        setIsAdding(false);
        return;
      }
      if (!data.name) {
        showNotification('Name is required', 'error');
        return;
      }
      const result = await assetService.addAssetType(data as Omit<AssetType, 'id'>, currentOrganization?.id);
      if (result.success) {
        await loadData();
        showNotification('Asset type added successfully', 'success');
        setIsAdding(false);
      } else {
        showNotification(`Error: ${result.error}`, 'error');
      }
    } else if (isEditing && selectedAssetType?.id) {
      const result = await assetService.updateAssetType(selectedAssetType.id, data);
      if (result.success) {
        await loadData();
        showNotification('Asset type updated successfully', 'success');
        setIsEditing(false);
      } else {
        showNotification(`Error: ${result.error}`, 'error');
      }
    }
  };

  const handleFormCancel = () => {
    setIsAdding(false);
    setIsEditing(false);
    setSelectedAssetType(null);
    setAssetTypeToAdd(null);
  };

  const handleAddFromSuggestion = (suggestion: NewAssetTypeSuggestion) => {
    // Check if user has permission to add asset types (only admin or manager)
    if (!isSystemManager) {
      showNotification('Only administrators and managers can add asset types', 'error');
      return;
    }
    if (!currentOrganization?.id) {
      setShowOrgModal(true);
      return;
    }

    // Convert AI suggestion to asset type format
    const assetTypeData: Partial<AssetType> = {
      name: suggestion.name,
      description: suggestion.description,
      complianceTemplateIds: suggestion.templateIds,
      customFields: suggestion.fields.map((field: any) => ({
        name: field.name,
        type: field.type as any,
        required: field.required || false,
        description: field.description
      }))
    };
    
    setAssetTypeToAdd(assetTypeData);
    setIsAdding(true);
    setSelectedAssetType(null);
    setIsEditing(false);
    // Clear search to see the new asset type after creation
    setSearchTerm('');
  };

  const handleAddAsset = (assetType: AssetType) => {
    setAssetTypeForAsset(assetType);
    setIsAddingAsset(true);
  };

  const handleAssetSubmitted = async () => {
    await loadData();
    setIsAddingAsset(false);
    setAssetTypeForAsset(null);
  };

  const handleAssetFormCancel = () => {
    setIsAddingAsset(false);
    setAssetTypeForAsset(null);
  };

  const handleBulkUploadCancel = () => {
    setIsBulkUploading(false);
    setAssetTypeForAsset(null);
  };

  const handleViewAssets = async (assetType: AssetType) => {
    if (!assetType.id) return;
    
    setIsViewingAssets(true);
    setAssetTypeForAsset(assetType);
    
    const result = await assetService.getAssetsByType(assetType.id, currentOrganization?.id);
    if (result.success && result.data) {
      const nonDeletedIds = new Set(locations.map(l => l.id).filter(Boolean));
      let assets = result.data.filter(asset =>
        asset.locationId && nonDeletedIds.has(asset.locationId)
      );
      if (isSystemUser && userLocationIds.length > 0) {
        assets = assets.filter(asset => userLocationIds.includes(asset.locationId));
      }
      setAssetsList(assets);
    } else {
      showNotification(`Error loading assets: ${result.error}`, 'error');
      setAssetsList([]);
    }
  };

  const handleViewAssetsCancel = () => {
    setIsViewingAssets(false);
    setAssetTypeForAsset(null);
    setAssetsList([]);
    setFilterLocation('all');
  };

  const handleDownloadQRSheet = () => {
    setIsQRSheetModalOpen(true);
    setSelectedQRSheetLocation('');
  };

  const handleGenerateQRSheet = async () => {
    if (!selectedQRSheetLocation || !currentOrganization?.id) {
      showNotification('Please select a location', 'error');
      return;
    }

    setIsGeneratingQRSheet(true);
    
    try {
      // Get all assets for the selected location
      const locationAssetsResult = await assetService.getAssetsByLocation(selectedQRSheetLocation, currentOrganization.id);
      if (!locationAssetsResult.success || !locationAssetsResult.data) {
        showNotification('Failed to load assets', 'error');
        return;
      }

      const locationAssets = locationAssetsResult.data;
      
      if (locationAssets.length === 0) {
        showNotification('No assets found for the selected location', 'error');
        return;
      }

      // Generate PDF with QR codes aligned for PLS-289MW label sheet
      // Paper: 8.5" x 11"
      // Stickers: 2" x 2" each, arranged in 4 columns x 5 rows (20 stickers per sheet)
      // Margins: 0.25" left/right, 0.5" top/bottom (equal margins on each side)
      // Stickers touch each other with no gaps between them
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'in',
        format: 'letter'
      });

      const locationName = locations.find(l => l.id === selectedQRSheetLocation)?.name || 'Unknown Location';
      
      // PLS-289MW label specifications
      const pageWidth = 8.5;
      const pageHeight = 11;
      // Sticker size: exactly 2.0" x 2.0" to match label sheet
      // Printer compensation: If PDF prints at 1.9" when specified as 2.0", scale factor = 1.9/2.0 = 0.95
      // To get exactly 2.0" printed, use: 2.0 / 0.95 = 2.105263... ≈ 2.105"
      const printerScaleFactor = 1.9 / 2.0; // Measured print size / desired size (0.95 if prints at 1.9" when PDF is 2")
      const stickerSize = 2.0 / printerScaleFactor; // Compensate for printer scaling to print exactly 2.0" x 2.0"
      const qrSize = 1.5 / printerScaleFactor; // Scale QR code proportionally to maintain 1.5" printed size
      
      // Margins for PLS-289MW - Equal margins on all sides
      // With 4 columns of 2" stickers on 8.5" paper:
      // - Stickers width: 4 * 2" = 8"
      // - Paper width: 8.5"
      // - Remaining horizontal: 0.5" total → 0.25" each side
      // With 5 rows of 2" stickers on 11" paper:
      // - Stickers height: 5 * 2" = 10"
      // - Paper height: 11"
      // - Remaining vertical: 1" total → 0.5" each side
      const leftMargin = 0.25;  // Left margin: 0.25" (equal to right)
      const rightMargin = 0.25; // Right margin: 0.25" (equal to left)
      const topMargin = 0.5;    // Top margin: 0.5" (equal to bottom)
      const bottomMargin = 0.5; // Bottom margin: 0.5" (equal to top)
      
      // Gap between labels (PLS-289MW specifications)
      // Horizontal (columns): NO gap between stickers - they touch each other
      // Vertical (rows): Gap between rows
      const horizontalGap = 0.0;  // No gap between columns (stickers touch)
      const verticalGap = 0.1;    // Gap between rows (doubled from 0.05" to 0.1")
      
      // Fine-tuning adjustments (adjust these based on test prints)
      const verticalOffset = 0.0; // Adjust in 0.01" increments (negative = move up, positive = move down)
      const horizontalOffset = 0.0; // Adjust in 0.01" increments (negative = move left, positive = move right)
      
      // Sticker layout: 4 columns x 5 rows
      const stickersPerRow = 4;
      const stickersPerCol = 5;
      const stickersPerPage = stickersPerRow * stickersPerCol; // 20 stickers per page
      
      // Recalculate margins to account for gaps
      // Horizontal: No gaps between columns (horizontalGap = 0), so stickers touch
      // Vertical: Gaps between rows (verticalGap > 0)
      // Total width needed: (stickersPerRow * stickerSize) + ((stickersPerRow - 1) * horizontalGap)
      // With horizontalGap = 0: totalWidthNeeded = 4 * 2" = 8" → leftMargin = (8.5 - 8) / 2 = 0.25"
      const totalWidthNeeded = (stickersPerRow * stickerSize) + ((stickersPerRow - 1) * horizontalGap);
      // Total height needed: (stickersPerCol * stickerSize) + ((stickersPerCol - 1) * verticalGap)
      // With 5 rows and gaps between them: totalHeightNeeded = (5 * 2") + (4 * verticalGap)
      const totalHeightNeeded = (stickersPerCol * stickerSize) + ((stickersPerCol - 1) * verticalGap);
      // Calculate margins to center the label grid (equal margins on each side)
      const calculatedLeftMargin = (pageWidth - totalWidthNeeded) / 2;
      const calculatedTopMargin = (pageHeight - totalHeightNeeded) / 2;
      
      // Vertical spacing within each 2" x 2" sticker for logo, QR code, and asset name
      // Total sticker height: 2"
      // Distribution: Logo (0.15"), QR code (1.5"), Asset name (0.35") = 2.0"
      const logoHeight = 0.15;      // Space for logo at top of sticker
      const qrVerticalOffset = 0.25; // QR code vertical offset (centers 1.5" QR in remaining space)
      const assetNameHeight = 0.35; // Space for asset name at bottom of sticker
      
      // QR code offset within each 2" sticker (to center 1.5" QR code horizontally)
      const qrOffsetInSticker = (stickerSize - qrSize) / 2; // 0.25" from sticker edges

      // Helper function to add a QR code to PDF
      const addQRCodeToPDF = async (asset: Asset, stickerCol: number, stickerRow: number) => {
        const qrUrl = `${window.location.origin}/asset/${asset.id}`;
        
        // Calculate sticker top-left position (with fine-tuning offsets and gaps)
        // IMPORTANT: All content (logo, QR code, asset name) must fit within this 2" x 2" sticker boundary
        // Position includes gaps between labels: margin + (column * stickerSize) + (column * gap)
        const stickerX = calculatedLeftMargin + (stickerCol * stickerSize) + (stickerCol * horizontalGap) + horizontalOffset;
        const stickerY = calculatedTopMargin + (stickerRow * stickerSize) + (stickerRow * verticalGap) + verticalOffset;
        
        // Add black boundary rectangle around each 2" x 2" sticker for troubleshooting
        pdf.setDrawColor(0, 0, 0); // Black color
        pdf.setLineWidth(0.01); // Thin line (0.01 inches)
        pdf.rect(stickerX, stickerY, stickerSize, stickerSize, 'S'); // 'S' = stroke (outline only)
        
        // Calculate center of sticker for logo and text
        const stickerCenterX = stickerX + stickerSize / 2;
        
        // Generate QR code as data URL using qrcode library
        const qrDataUrl = await QRCode.toDataURL(qrUrl, {
          width: 288, // High resolution
          margin: 2,
          errorCorrectionLevel: 'H'
        });
        
        // Vertical distribution within 2" sticker:
        // - Logo: Top 0.15" (stickerY to stickerY + 0.15)
        // - QR code: Middle 1.5" (stickerY + 0.15 to stickerY + 1.65)
        // - Asset name: Bottom 0.35" (stickerY + 1.65 to stickerY + 2.0)
        // Total: 0.15 + 1.5 + 0.35 = 2.0" ✓
        
        // Logo position: at top of sticker (within first 0.15" of sticker)
        const logoY = stickerY + 0.08; // Centered in logo area (0.15" / 2 ≈ 0.075, add small offset)
        
        // Calculate logo dimensions to center it horizontally
        const circleRadius = 0.05;
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'bold');
        const textWidth = pdf.getTextWidth('complin');
        const gap = 0.03; // Gap between circle and text
        const totalLogoWidth = circleRadius * 2 + gap + textWidth;
        const logoStartX = stickerCenterX - totalLogoWidth / 2;
        
        // Add logo (black circle) - centered in sticker horizontally, at top vertically
        pdf.setFillColor(0, 0, 0);
        pdf.circle(logoStartX + circleRadius, logoY, circleRadius, 'F');
        
        // Add logo text - centered in sticker horizontally
        pdf.setTextColor(0, 0, 0);
        pdf.text('complin', logoStartX + circleRadius * 2 + gap, logoY + 0.03, { align: 'left' });
        
        // QR code position: below logo, centered horizontally
        // QR starts at stickerY + 0.15 (right after logo area)
        // QR is 1.5" tall, so it goes from stickerY + 0.15 to stickerY + 1.65
        // This leaves 0.35" at bottom for asset name
        const qrX = stickerX + qrOffsetInSticker; // Centered horizontally (0.25" from left edge of sticker)
        const qrY = stickerY + logoHeight; // Starts right after logo (0.15" from top of sticker)
        
        // Add QR code (centered horizontally, positioned below logo)
        pdf.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);
        
        // Asset name position: at bottom of sticker (within last 0.35" of sticker)
        // Position it near the bottom but within sticker boundary
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'normal');
        const assetNameText = asset.assetName.length > 20 
          ? asset.assetName.substring(0, 20) + '...' 
          : asset.assetName;
        // Position asset name at stickerY + 1.75 (leaving 0.25" margin from bottom)
        // This ensures it's within the sticker and doesn't overlap with next row
        const assetNameY = stickerY + stickerSize - 0.25; // 0.25" from bottom of sticker
        pdf.text(assetNameText.toLowerCase(), stickerCenterX, assetNameY, {
          align: 'center',
          maxWidth: stickerSize - 0.2
        });
      };

      // Process assets and add to PDF
      for (let i = 0; i < locationAssets.length; i++) {
        const asset = locationAssets[i];
        
        // Calculate which page, row, and column for this asset
        const pageIndex = Math.floor(i / stickersPerPage);
        const indexOnPage = i % stickersPerPage;
        const stickerRow = Math.floor(indexOnPage / stickersPerRow);
        const stickerCol = indexOnPage % stickersPerRow;
        
        // Add new page if needed
        if (pageIndex > 0 && i % stickersPerPage === 0) {
          pdf.addPage();
        }
        
        // Add QR code at calculated sticker position
        await addQRCodeToPDF(asset, stickerCol, stickerRow);
      }

      // Save PDF
      const fileName = `QR-Sheet-${locationName}-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
      showNotification(`QR sheet generated with ${locationAssets.length} assets`, 'success');
      setIsQRSheetModalOpen(false);
      setSelectedQRSheetLocation('');
    } catch (error: any) {
      console.error('Error generating QR sheet:', error);
      showNotification(`Error generating QR sheet: ${error.message}`, 'error');
    } finally {
      setIsGeneratingQRSheet(false);
    }
  };

  const handleDeleteAsset = async (assetId: string, skipConfirmation: boolean = false) => {
    // Check if user has permission to delete assets (only admin or manager)
    if (isSystemUser) {
      showNotification('Only administrators and managers can delete assets', 'error');
      return false;
    }
    // Confirmation is handled by modals, no browser confirm needed
    const userInfo = user ? { id: user.id, name: user.name || 'Unknown', email: user.email || 'Unknown' } : undefined;
    const result = await assetService.deleteAsset(assetId, userInfo, currentOrganization?.id);
    if (result.success) {
      // Only reload assets list and refresh counts if not in bulk delete mode
      if (!skipConfirmation) {
        // Reload assets list (only non-deleted locations)
        if (assetTypeForAsset?.id) {
          const assetsResult = await assetService.getAssetsByType(assetTypeForAsset.id, currentOrganization?.id);
          if (assetsResult.success && assetsResult.data) {
            const nonDeletedIds = new Set(locations.map(l => l.id).filter(Boolean));
            let assets = assetsResult.data.filter(asset =>
              asset.locationId && nonDeletedIds.has(asset.locationId)
            );
            if (isSystemUser && userLocationIds.length > 0) {
              assets = assets.filter(asset => userLocationIds.includes(asset.locationId));
            }
            setAssetsList(assets);
          }
        }
        await loadData(); // Refresh asset counts
        showNotification('Asset deleted successfully', 'success');
      }
    } else {
      if (!skipConfirmation) {
        showNotification(`Error: ${result.error}`, 'error');
      }
    }
    return result.success;
  };

  // Filter asset types
  const filteredAssetTypes = assetTypes.filter(assetType => {
    // Apply search filter
    const matchesSearch = assetType.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      assetType.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;
    
    // For users with system role "User", filter by owner roles AND location access
    if (isSystemUser) {
      // If asset type has no compliance templates, don't show it to regular users
      if (!assetType.complianceTemplateIds || assetType.complianceTemplateIds.length === 0) {
        return false;
      }
      
      // Check if at least one compliance template's owner_role matches user's owner roles
      const hasMatchingOwnerRole = assetType.complianceTemplateIds.some(templateId => {
        const template = templates[templateId];
        if (!template) return false;
        
        const templateOwnerRole = template.owner_role;
        if (!templateOwnerRole) return false;
        
        return userOwnerRoles.includes(templateOwnerRole);
      });
      
      if (!hasMatchingOwnerRole) return false;
      
      // System users can see asset types that match their owner roles
      // They don't need assets to exist - they can view the asset type and add assets
      return true;
    }
    
    // Admin and Manager system roles see all asset types
    return true;
  });

  return (
    <div className="p-4 max-w-[95vw] mx-auto relative">
      {/* Backdrop overlay when panel is open */}
      {(isAdding || isEditing || isAddingAsset || isBulkUploading || isViewingAssets || isQRSheetModalOpen) && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-30 z-40 transition-opacity"
          onClick={isQRSheetModalOpen ? () => setIsQRSheetModalOpen(false) : isAddingAsset ? handleAssetFormCancel : isBulkUploading ? handleBulkUploadCancel : isViewingAssets ? handleViewAssetsCancel : handleFormCancel}
        />
      )}
      
      {/* Main Content */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-semibold text-gray-800 flex items-center gap-2">
              <Package className="w-7 h-7 text-primary-600" />
              Asset Management
            </h2>
            <ContextualHelp category="Assets" tooltip="Help with Assets" />
          </div>
          <div className="flex gap-2">
            {hasFeature('qr_codes') ? (
              <button onClick={handleDownloadQRSheet} className="btn-secondary px-4 py-2 flex items-center gap-2">
                <Download className="w-4 h-4" /> Download QR Sheet
              </button>
            ) : (
              <FeatureLock feature="qr_codes" showUpgradePrompt={false}>
                <button disabled className="btn-secondary px-4 py-2 flex items-center gap-2 opacity-50 cursor-not-allowed">
                  <Download className="w-4 h-4" /> Download QR Sheet
                </button>
              </FeatureLock>
            )}
            {isSystemManager && (
              <button onClick={handleAdd} className="btn-primary px-4 py-2 flex items-center gap-2">
                <Plus className="w-4 h-4" /> Add Asset Type
              </button>
            )}
            <button onClick={loadData} className="btn-secondary px-4 py-2">
              Refresh
            </button>
          </div>
        </div>

        {/* Info Banner */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>How it works:</strong> Create asset types (e.g., Fire Extinguishers, Emergency Lights) and tie them to compliance items. Then add or upload values for each asset type per location.
          </p>
        </div>

        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search asset types... (AI-powered suggestions available)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md pr-10"
            />
            {isLoadingAI && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-orange-500 animate-spin" />
                <span className="text-xs text-orange-600 font-medium animate-pulse">AI searching...</span>
              </div>
            )}
          </div>
        </div>

        {/* Asset Types Table */}
        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-gray-600 mt-2">Loading asset types...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto border-collapse">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Asset Type</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Description</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">Compliance Items</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 border border-gray-200">Assets</th>
                  <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 border border-gray-200">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAssetTypes.map((assetType, index) => (
                  <tr key={assetType.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100`}>
                    <td className="px-3 py-2 border border-gray-200">
                      <div className="font-medium text-sm text-gray-900">{assetType.name}</div>
                    </td>
                    <td className="px-3 py-2 border border-gray-200">
                      <span className="text-xs text-gray-700">{assetType.description || 'No description'}</span>
                    </td>
                    <td className="px-3 py-2 border border-gray-200">
                      {assetType.complianceTemplateIds && assetType.complianceTemplateIds.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {assetType.complianceTemplateIds.map((templateId, idx) => {
                            const template = templates[templateId];
                            return (
                              <span 
                                key={idx} 
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800"
                                title={template ? `${template.name} • ${template.category}` : templateId}
                              >
                                {template ? template.name : templateId}
                              </span>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-500">None</span>
                      )}
                    </td>
                    <td className="px-3 py-2 border border-gray-200 text-center">
                      <button
                        onClick={() => handleViewAssets(assetType)}
                        className="text-blue-600 hover:text-blue-800 font-semibold text-sm hover:underline"
                        title="View Assets"
                      >
                        {assetCounts[assetType.id || ''] || 0}
                      </button>
                    </td>
                    <td className="px-3 py-2 border border-gray-200">
                      <div className="flex justify-center gap-1">
                        <button
                          onClick={() => handleAddAsset(assetType)}
                          className="border border-blue-600 text-blue-600 hover:bg-blue-50 text-xs font-medium px-3 py-1.5 rounded"
                          title="Add Assets"
                        >
                          Add Assets
                        </button>
                        {isSystemManager && (
                          <>
                            <button
                              onClick={() => handleEdit(assetType)}
                              className="text-green-600 hover:text-green-800 text-xs px-2 py-1"
                              title="Edit"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(assetType.id!)}
                              className="text-red-600 hover:text-red-800 text-xs px-2 py-1"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {filteredAssetTypes.length === 0 && !isLoading && (
          <div>
            {isLoadingAI ? (
              <FeatureLock feature="ai_assistance" showUpgradePrompt={false}>
                <div className="text-center py-8 border border-gray-200 rounded">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Sparkles className="w-6 h-6 text-orange-500 animate-spin" />
                    <p className="text-lg font-medium text-gray-700">AI is finding suggestions...</p>
                  </div>
                  <p className="text-sm text-gray-500">Analyzing available templates for you</p>
                </div>
              </FeatureLock>
            ) : newAssetTypeSuggestions && newAssetTypeSuggestions.length === 0 && searchTerm.length >= 3 ? (
              <FeatureLock feature="ai_assistance">
                <div className="border border-orange-200 rounded-lg p-6 bg-orange-50 text-left">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="w-5 h-5 text-orange-600" />
                    <h3 className="text-lg font-semibold text-orange-900">AI Search Complete</h3>
                  </div>
                  
                  <p className="text-sm text-gray-700 mb-2">
                    {Object.keys(templates).length === 0 
                      ? '⚠️ No compliance templates available. Please create templates first before creating asset types.'
                      : 'No AI-generated asset type suggestions found. Try a different search term or create an asset type manually.'}
                  </p>
                  
                  {Object.keys(templates).length === 0 && (
                    <button
                      onClick={() => window.location.href = '/compliance-management'}
                      className="mt-3 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors text-sm"
                    >
                      Go to Templates →
                    </button>
                  )}
                </div>
              </FeatureLock>
            ) : newAssetTypeSuggestions && newAssetTypeSuggestions.length > 0 ? (
              <FeatureLock feature="ai_assistance">
                <div className="border border-blue-200 rounded-lg p-6 bg-blue-50 text-left">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="w-5 h-5 text-blue-600" />
                    <h3 className="text-lg font-semibold text-blue-900">New Asset Type Suggestions</h3>
                  </div>
                  
                  <p className="text-sm text-gray-700 mb-4">
                    Based on your search, here are asset types you might want to add to your system:
                  </p>
                  
                  <div className="space-y-4">
                    {newAssetTypeSuggestions.map((suggestion, index) => (
                    <div 
                      key={index}
                      className="bg-white rounded-lg p-5 border border-blue-200 hover:border-blue-300 hover:shadow-md transition-all"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h5 className="font-semibold text-gray-900 text-lg mb-1">{suggestion.name}</h5>
                          {suggestion.description && (
                            <p className="text-sm text-gray-700 mb-2">{suggestion.description}</p>
                          )}
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            {suggestion.templateNames.map((tname, idx) => (
                              <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                                {tname}
                              </span>
                            ))}
                          </div>
                          <p className="text-xs text-gray-600 italic mb-3">"{suggestion.why_relevant}"</p>
                          
                          {suggestion.fields && suggestion.fields.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <div className="text-xs text-gray-700">
                                <span className="font-medium">Custom Fields ({suggestion.fields.length}):</span>
                                <ul className="mt-1 space-y-1 ml-4 list-disc">
                                  {suggestion.fields.slice(0, 6).map((field, idx) => (
                                    <li key={idx} className="text-gray-600">
                                      {field.name} <span className="text-gray-400">({field.type})</span>
                                      {field.required && <span className="text-red-600 ml-1">*</span>}
                                    </li>
                                  ))}
                                  {suggestion.fields.length > 6 && (
                                    <li className="text-gray-500 italic">... and {suggestion.fields.length - 6} more fields</li>
                                  )}
                                </ul>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {isSystemManager && (
                        <button
                          onClick={() => handleAddFromSuggestion(suggestion)}
                          className="w-full mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-medium"
                        >
                          <Plus className="w-4 h-4" />
                          Add This Asset Type
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                </div>
              </FeatureLock>
            ) : (
              <div className="text-center py-8 text-gray-500 border border-gray-200 rounded">
                <Package className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p className="text-lg font-medium">No asset types found</p>
                <p className="text-sm mt-1">
                  {searchTerm 
                    ? 'Try a different search term or clear filters.' 
                    : 'Start by adding your first asset type.'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Form Modal */}
      {(isAdding || isEditing) && (
        <div className="fixed inset-y-0 right-0 w-[600px] bg-white shadow-2xl border-l border-gray-200 z-[120] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-800">
              {selectedAssetType ? 'Edit Asset Type' : 'Add New Asset Type'}
            </h2>
            <button
              onClick={handleFormCancel}
              className="text-gray-500 hover:text-gray-700 p-1"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form Content - Scrollable */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-4">
              <AssetTypeForm
                assetType={(assetTypeToAdd && assetTypeToAdd.name) ? assetTypeToAdd as AssetType : (selectedAssetType || undefined)}
                onSubmit={handleFormSubmit}
                onCancel={handleFormCancel}
              />
            </div>
          </div>
        </div>
      )}

      {/* Add Asset Modal */}
      {isAddingAsset && assetTypeForAsset && (
        <div className="fixed inset-y-0 right-0 w-[600px] bg-white shadow-2xl border-l border-gray-200 z-[120] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-800">
              Add New Asset
            </h2>
            <button
              onClick={handleAssetFormCancel}
              className="text-gray-500 hover:text-gray-700 p-1"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form Content - Scrollable */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-4">
              <AssetForm
                assetType={assetTypeForAsset}
                onSubmit={handleAssetSubmitted}
                onCancel={handleAssetFormCancel}
                stayOpenAfterSubmit={true}
                onSuccess={async () => {
                  // Refresh asset counts without closing the form
                  await loadData();
                }}
                onBulkUploadClick={() => {
                  setIsAddingAsset(false);
                  setIsBulkUploading(true);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {isBulkUploading && assetTypeForAsset && (
        <div className="fixed inset-y-0 right-0 w-[600px] bg-white shadow-2xl border-l border-gray-200 z-[120] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              Add with AI
            </h2>
            <button
              onClick={handleBulkUploadCancel}
              className="text-gray-500 hover:text-gray-700 p-1"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form Content - Scrollable */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-4">
              <BulkUploadAsset
                assetType={assetTypeForAsset}
                onSubmit={async () => {
                  await loadData();
                  handleBulkUploadCancel();
                }}
                onCancel={handleBulkUploadCancel}
              />
            </div>
          </div>
        </div>
      )}

      {/* View Assets Modal */}
      {isViewingAssets && assetTypeForAsset && (
        <ViewAssetsModal
          assetType={assetTypeForAsset}
          assets={assetsList}
          locations={
            isSystemUser && userLocationIds.length > 0
              ? locations.filter(loc => loc.id && userLocationIds.includes(loc.id))
              : locations
          }
          filterLocation={filterLocation}
          onFilterLocationChange={setFilterLocation}
          onDelete={handleDeleteAsset}
          onClose={handleViewAssetsCancel}
          isSystemUser={isSystemUser}
          onBulkDeleteComplete={async () => {
            // Reload assets list after bulk delete (only non-deleted locations)
            if (assetTypeForAsset?.id) {
              const assetsResult = await assetService.getAssetsByType(assetTypeForAsset.id, currentOrganization?.id);
              if (assetsResult.success && assetsResult.data) {
                const nonDeletedIds = new Set(locations.map(l => l.id).filter(Boolean));
                let assets = assetsResult.data.filter(asset =>
                  asset.locationId && nonDeletedIds.has(asset.locationId)
                );
                if (isSystemUser && userLocationIds.length > 0) {
                  assets = assets.filter(asset => userLocationIds.includes(asset.locationId));
                }
                setAssetsList(assets);
              }
            }
            await loadData(); // Refresh asset counts
          }}
          onAssetUpdated={async () => {
            // Reload assets list after floor tag update
            if (assetTypeForAsset?.id) {
              const assetsResult = await assetService.getAssetsByType(assetTypeForAsset.id, currentOrganization?.id);
              if (assetsResult.success && assetsResult.data) {
                const nonDeletedIds = new Set(locations.map(l => l.id).filter(Boolean));
                let assets = assetsResult.data.filter(asset =>
                  asset.locationId && nonDeletedIds.has(asset.locationId)
                );
                if (isSystemUser && userLocationIds.length > 0) {
                  assets = assets.filter(asset => userLocationIds.includes(asset.locationId));
                }
                setAssetsList(assets);
              }
            }
          }}
        />
      )}

      {/* QR Sheet Download Modal */}
      {isQRSheetModalOpen && (
        <div className="fixed inset-y-0 right-0 w-[500px] bg-white shadow-2xl border-l border-gray-200 z-[120] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-800">Download QR Sheet</h2>
            <button
              onClick={() => setIsQRSheetModalOpen(false)}
              className="text-gray-500 hover:text-gray-700 p-1"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-4">
                Select a location to generate a PDF sheet with QR codes for all assets in that location.
              </p>
              
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location
              </label>
              <select
                value={selectedQRSheetLocation}
                onChange={(e) => setSelectedQRSheetLocation(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                disabled={isGeneratingQRSheet}
              >
                <option value="">Select a location...</option>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            </div>

            {selectedQRSheetLocation && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-800">
                  This will generate a PDF with QR codes for all assets in the selected location. 
                  Each QR code includes the Complin logo and asset name.
                </p>
              </div>
            )}
          </div>

          <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-2">
            <button
              onClick={() => setIsQRSheetModalOpen(false)}
              className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={isGeneratingQRSheet}
            >
              Cancel
            </button>
            <button
              onClick={handleGenerateQRSheet}
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              disabled={!selectedQRSheetLocation || isGeneratingQRSheet}
            >
              {isGeneratingQRSheet ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Generating...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Generate PDF
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Organization Required Modal */}
      {showOrgModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[200] flex items-center justify-center p-4"
          onClick={() => setShowOrgModal(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Organization Required</h3>
                <p className="text-sm text-gray-600">Action Required</p>
              </div>
            </div>
            
            <p className="text-sm text-gray-700 mb-6">
              You must have an organization to add asset types. Please create an organization first.
            </p>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowOrgModal(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowOrgModal(false);
                  navigate('/organizations');
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors text-sm font-medium inline-flex items-center gap-2"
              >
                Go to Organizations
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Asset Type Confirmation Modal */}
      {showDeleteAssetTypeConfirm && assetTypeToDelete && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[200] flex items-center justify-center p-4"
          onClick={() => {
            setShowDeleteAssetTypeConfirm(false);
            setAssetTypeToDelete(null);
          }}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Delete Asset Type
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Are you sure you want to delete this asset type? This will also delete all associated asset values.
                </p>
                <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                  <p className="text-xs text-red-800">
                    <strong>Warning:</strong> This action cannot be undone. This will permanently delete the asset type and all associated assets and their data.
                  </p>
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowDeleteAssetTypeConfirm(false);
                      setAssetTypeToDelete(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteAssetTypeConfirm}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Delete Asset Type
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Floating Getting Started button on empty state */}
      {assetTypes.length === 0 && !isLoading && !showOrgModal && (
        <a
          href="/help/getting-started"
          className="fixed bottom-24 right-4 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg px-4 py-2 z-50"
          title="Getting Started"
        >
          Getting Started
        </a>
      )}
      </div>
  );
};

// View Assets Modal Component
interface ViewAssetsModalProps {
  assetType: AssetType;
  assets: Asset[];
  locations: Location[];
  filterLocation: string;
  onFilterLocationChange: (locationId: string) => void;
  onDelete: (assetId: string, skipConfirmation?: boolean) => Promise<boolean>;
  onClose: () => void;
  onBulkDeleteComplete?: () => Promise<void>; // Callback to reload assets after bulk delete
  onAssetUpdated?: () => void | Promise<void>; // Callback when asset is updated (e.g. floor tag)
  isSystemUser?: boolean; // Whether the user is a system user (can't delete)
}

const ViewAssetsModal: React.FC<ViewAssetsModalProps> = ({
  assetType,
  assets,
  locations,
  filterLocation,
  onFilterLocationChange,
  onDelete,
  onClose,
  onBulkDeleteComplete,
  onAssetUpdated,
  isSystemUser = false
}) => {
  const { showNotification } = useNotification();
  const { currentOrganization } = useOrganization();
  const [showImageModal, setShowImageModal] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [floorMap, setFloorMap] = useState<Record<string, Floor>>({});
  const [selectedAssets, setSelectedAssets] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<string>('assetName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showSingleDeleteConfirm, setShowSingleDeleteConfirm] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState<string | null>(null);
  const [assetToTagFloor, setAssetToTagFloor] = useState<Asset | null>(null);

  // Load floor names for assets that are tagged on floor map
  useEffect(() => {
    if (!currentOrganization?.id || assets.length === 0) return;
    const locationIdsWithFloors = [...new Set(
      assets.filter(a => a.floorId).map(a => a.locationId).filter(Boolean)
    )] as string[];
    if (locationIdsWithFloors.length === 0) {
      setFloorMap({});
      return;
    }
    const load = async () => {
      const map: Record<string, Floor> = {};
      for (const locId of locationIdsWithFloors) {
        const r = await floorService.getFloorsByLocation(locId, currentOrganization.id);
        if (r.success && r.data) {
          for (const f of r.data) {
            if (f.id) map[f.id] = f;
          }
        }
      }
      setFloorMap(map);
    };
    load();
  }, [assets, currentOrganization?.id]);

  // Filter assets by location
  const locationFilteredAssets = filterLocation === 'all' 
    ? assets 
    : assets.filter(asset => asset.locationId === filterLocation);

  // Filter by search term
  const filteredAssets = locationFilteredAssets.filter(asset => {
    const matchesSearch = !searchTerm || (
      asset.assetName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (asset.assetLocation && asset.assetLocation.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (asset.floorId && getFloorName(asset.floorId).toLowerCase().includes(searchTerm.toLowerCase()))
    );
    return matchesSearch;
  });

  const getLocationName = (locationId: string) => {
    const location = locations.find(l => l.id === locationId);
    return location ? location.name : locationId;
  };

  const getFloorName = (floorId: string) => {
    return floorMap[floorId]?.name || floorId;
  };

  // Sort assets
  const sortedAssets = [...filteredAssets].sort((a, b) => {
    let comparison = 0;
    
    switch (sortField) {
      case 'assetName':
        comparison = a.assetName.localeCompare(b.assetName);
        break;
      case 'location':
        comparison = getLocationName(a.locationId).localeCompare(getLocationName(b.locationId));
        break;
      case 'assetLocation':
        comparison = (a.assetLocation || '').localeCompare(b.assetLocation || '');
        break;
      case 'floorMap':
        comparison = (a.floorId ? getFloorName(a.floorId) : '').localeCompare(b.floorId ? getFloorName(b.floorId) : '');
        break;
      default:
        comparison = 0;
    }
    
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const toggleSelect = (assetId: string) => {
    setSelectedAssets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(assetId)) {
        newSet.delete(assetId);
      } else {
        newSet.add(assetId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedAssets.size === sortedAssets.length) {
      setSelectedAssets(new Set());
    } else {
      setSelectedAssets(new Set(sortedAssets.map(a => a.id!)));
    }
  };

  const handleBulkDelete = () => {
    // Check if user has permission to delete assets (only admin or manager)
    if (isSystemUser) {
      showNotification('Only administrators and managers can delete assets', 'error');
      return;
    }
    if (selectedAssets.size === 0) {
      return;
    }
    setShowBulkDeleteConfirm(true);
  };

  const handleBulkDeleteConfirm = async () => {
    setShowBulkDeleteConfirm(false);
    
    // Delete all selected assets without showing individual confirmations
    let successCount = 0;
    let failCount = 0;
    
    for (const assetId of selectedAssets) {
      const success = await onDelete(assetId, true); // Pass skipConfirmation: true
      if (success) {
        successCount++;
      } else {
        failCount++;
      }
    }
    
    setSelectedAssets(new Set());
    
    // Show summary notification
    if (failCount === 0) {
      showNotification(`Successfully deleted ${successCount} asset(s)`, 'success');
    } else if (successCount === 0) {
      showNotification(`Failed to delete ${failCount} asset(s)`, 'error');
    } else {
      showNotification(`Deleted ${successCount} asset(s), failed to delete ${failCount} asset(s)`, 'info');
    }
    
    // Reload assets list and refresh counts via callback
    if (onBulkDeleteComplete) {
      await onBulkDeleteComplete();
    }
  };

  const handleSingleDelete = (assetId: string) => {
    // Check if user has permission to delete assets (only admin or manager)
    if (isSystemUser) {
      showNotification('Only administrators and managers can delete assets', 'error');
      return;
    }
    setAssetToDelete(assetId);
    setShowSingleDeleteConfirm(true);
  };

  const handleSingleDeleteConfirm = async () => {
    if (!assetToDelete) return;
    setShowSingleDeleteConfirm(false);
    await onDelete(assetToDelete, false);
    setAssetToDelete(null);
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) {
      return <ChevronUp className="w-3 h-3 text-gray-300" />;
    }
    return sortDirection === 'asc' 
      ? <ChevronUp className="w-3 h-3 text-primary-600" />
      : <ChevronDown className="w-3 h-3 text-primary-600" />;
  };

  return (
    <div className="fixed inset-y-0 right-0 w-[900px] bg-white shadow-2xl border-l border-gray-200 z-[120] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
        <h2 className="text-lg font-semibold text-gray-800">
          Assets: {assetType.name} ({filteredAssets.length})
        </h2>
        <div className="flex items-center gap-3">
          {selectedAssets.size > 0 && !isSystemUser && (
            <button
              onClick={handleBulkDelete}
              className="px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Delete Selected ({selectedAssets.size})
            </button>
          )}
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-1"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="p-4 border-b border-gray-200 bg-white">
        <div className="flex items-end gap-4">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Filter by Location
            </label>
            <select
              value={filterLocation}
              onChange={(e) => onFilterLocationChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Locations</option>
              {locations.map(location => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search assets..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Assets Table - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4">
          {sortedAssets.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Package className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-lg font-medium">No assets found</p>
              <p className="text-sm mt-1">
                {filterLocation === 'all' && !searchTerm
                  ? 'This asset type has no assets yet.' 
                  : 'No assets match your filters.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full table-auto border-collapse">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-2 text-center border border-gray-200">
                      <input
                        type="checkbox"
                        checked={selectedAssets.size === sortedAssets.length && sortedAssets.length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">
                      <button
                        onClick={() => handleSort('assetName')}
                        className="flex items-center gap-1 hover:text-primary-600"
                      >
                        Asset Name
                        <SortIcon field="assetName" />
                      </button>
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">
                      <button
                        onClick={() => handleSort('location')}
                        className="flex items-center gap-1 hover:text-primary-600"
                      >
                        Location
                        <SortIcon field="location" />
                      </button>
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">
                      <button
                        onClick={() => handleSort('assetLocation')}
                        className="flex items-center gap-1 hover:text-primary-600"
                      >
                        Asset Location
                        <SortIcon field="assetLocation" />
                      </button>
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border border-gray-200">
                      <button
                        onClick={() => handleSort('floorMap')}
                        className="flex items-center gap-1 hover:text-primary-600"
                      >
                        Mapped to Floor
                        <SortIcon field="floorMap" />
                      </button>
                    </th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 border border-gray-200">Photo</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 border border-gray-200">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedAssets.map((asset, index) => (
                    <tr key={asset.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100`}>
                      <td className="px-2 py-2 text-center border border-gray-200">
                        <input
                          type="checkbox"
                          checked={asset.id ? selectedAssets.has(asset.id) : false}
                          onChange={() => asset.id && toggleSelect(asset.id)}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                      </td>
                      <td className="px-3 py-2 border border-gray-200">
                        <div className="font-medium text-sm text-gray-900 cursor-pointer hover:text-primary-600 flex items-center gap-2"
                          onClick={() => asset.id && window.open(`/asset/${asset.id}`, '_blank')}
                        >
                          <ExternalLink className="w-4 h-4" />
                          {asset.assetName}
                        </div>
                      </td>
                      <td className="px-3 py-2 border border-gray-200">
                        <div className="text-xs text-gray-700">{getLocationName(asset.locationId)}</div>
                      </td>
                      <td className="px-3 py-2 border border-gray-200">
                        <div className="text-xs text-gray-700">{asset.assetLocation || '-'}</div>
                      </td>
                      <td className="px-3 py-2 border border-gray-200">
                        <div className="text-xs">
                          {asset.floorId ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-green-100 text-green-800">
                              <MapPin className="w-3 h-3" />
                              {getFloorName(asset.floorId)}
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 border border-gray-200 text-center">
                        {asset.photoUrl ? (
                          <button
                            onClick={() => setShowImageModal(asset.photoUrl || null)}
                            className="inline-block w-10 h-10 rounded border border-gray-200 overflow-hidden hover:opacity-80 transition-opacity"
                          >
                            <img
                              src={asset.photoUrl}
                              alt={asset.assetName}
                              className="w-full h-full object-cover"
                            />
                          </button>
                        ) : (
                          <Image className="w-6 h-6 mx-auto text-gray-300" />
                        )}
                      </td>
                      <td className="px-3 py-2 border border-gray-200 text-center">
                        <button
                          onClick={() => asset.locationId && setAssetToTagFloor(asset)}
                          className="text-purple-600 hover:text-purple-800 px-2 py-1"
                          title="Tag on floor map"
                        >
                          <MapPin className="w-4 h-4" />
                        </button>
                        {!isSystemUser && (
                          <button
                            onClick={() => asset.id && handleSingleDelete(asset.id)}
                            className="text-red-600 hover:text-red-800 px-2 py-1"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Image Modal */}
      {showImageModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 z-[150] flex items-center justify-center p-4"
          onClick={() => setShowImageModal(null)}
        >
          <div className="max-w-4xl max-h-[90vh] relative">
            <button
              onClick={() => setShowImageModal(null)}
              className="absolute top-4 right-4 bg-white p-2 rounded-full hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
            <img
              src={showImageModal}
              alt="Asset photo"
              className="max-w-full max-h-[90vh] rounded-lg"
            />
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[200] flex items-center justify-center p-4"
          onClick={() => setShowBulkDeleteConfirm(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Delete Assets
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Are you sure you want to delete <span className="font-semibold text-gray-900">{selectedAssets.size} asset(s)</span>? This action cannot be undone.
                </p>
                <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                  <p className="text-xs text-red-800">
                    <strong>Warning:</strong> This will permanently delete the selected assets and all associated data. This action cannot be undone.
                  </p>
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowBulkDeleteConfirm(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBulkDeleteConfirm}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Delete {selectedAssets.size} Asset(s)
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Single Delete Confirmation Modal */}
      {showSingleDeleteConfirm && assetToDelete && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[200] flex items-center justify-center p-4"
          onClick={() => {
            setShowSingleDeleteConfirm(false);
            setAssetToDelete(null);
          }}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Delete Asset
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Are you sure you want to delete this asset? This action cannot be undone.
                </p>
                <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                  <p className="text-xs text-red-800">
                    <strong>Warning:</strong> This will permanently delete the asset and all associated data. This action cannot be undone.
                  </p>
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowSingleDeleteConfirm(false);
                      setAssetToDelete(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSingleDeleteConfirm}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Delete Asset
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Floor Map Tag Modal */}
      {assetToTagFloor && locations.find(l => l.id === assetToTagFloor.locationId) && (
        <AssetFloorTagModal
          asset={assetToTagFloor}
          location={locations.find(l => l.id === assetToTagFloor.locationId)!}
          onClose={() => setAssetToTagFloor(null)}
          onSaved={async () => {
            if (onAssetUpdated) await onAssetUpdated();
          }}
        />
      )}
    </div>
  );
};

export default AssetManagement;

